import React from 'react';
import { useTranslation } from 'react-i18next';
import type { CertificationsAnalysis } from '../../types';

interface CertificationsSectionProps {
  data: CertificationsAnalysis;
}

const CertificationsSection: React.FC<CertificationsSectionProps> = ({ data }) => {
  const { t } = useTranslation();
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-5 flex items-center">
        <span className="text-2xl mr-3">📜</span>
        <h3 className="text-xl font-semibold">{t('results.certifications.title')}</h3>
        <span className="ml-auto text-xl font-bold">{data?.score}%</span>
      </div>
      
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {data?.matching?.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-green-500">
              <h4 className="font-semibold text-lg mb-3 flex items-center text-gray-800">
                <span className="mr-2">✅</span>
                {t('results.certifications.matching', { count: data?.matching?.length })}
              </h4>
              
              <div className="space-y-2">
                {data?.matching?.map((item, index) => (
                  <div key={index} className="flex items-center p-2 bg-green-50 rounded-lg">
                    <span className="text-green-600 mr-2">✓</span>
                    <div>
                      <div className="font-medium">{item?.found}</div>
                      {item?.required !== item?.found && (
                        <div className="text-xs text-gray-500">{t('results.certifications.required', { required: item?.required })}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {data?.matching?.length === 0 && (
            <p className="text-gray-500 italic">{t('results.certifications.noMatching')}</p>
          )}
          
          {data?.missing?.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-red-500">
              <h4 className="font-semibold text-lg mb-3 flex items-center text-gray-800">
                <span className="mr-2">❌</span>
                {t('results.certifications.missing', { count: data?.missing?.length })}
              </h4>
              
              <div className="space-y-2">
                {data?.missing?.map((cert, index) => (
                  <div key={index} className="flex items-center p-2 bg-red-50 rounded-lg">
                    <span className="text-red-600 mr-2">•</span>
                    <div className="font-medium">{cert}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {data?.missing?.length === 0 && (
            <p className="text-green-600 font-medium">{t('results.certifications.noMissing')}</p>
          )}
          
          {data?.analysis?.bonusCertifications?.length > 0 && (
            <div className={`bg-gray-50 rounded-lg p-5 border-l-4 border-blue-500 ${data?.matching?.length === 0 && data?.missing?.length === 0 ? 'md:col-span-2' : ''}`}>
              <h4 className="font-semibold text-lg mb-3 flex items-center text-gray-800">
                <span className="mr-2">🎁</span>
                {t('results.certifications.bonus', { count: data?.analysis?.bonusCertifications?.length })}
              </h4>
              
              <div className="space-y-2">
                {data?.analysis?.bonusCertifications?.map((cert, index) => (
                  <div key={index} className="flex items-center p-2 bg-blue-50 rounded-lg">
                    <span className="text-blue-600 mr-2">+</span>
                    <div className="font-medium">{cert}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {data?.matching?.length === 0 && data?.missing?.length === 0 && data?.analysis?.bonusCertifications?.length === 0 && (
            <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-gray-400">
              <h4 className="font-semibold text-lg mb-3 flex items-center text-gray-800">
                <span className="mr-2">ℹ️</span>
                {t('results.certifications.infoTitle')}
              </h4>
              <p className="text-gray-600">
                {t('results.certifications.infoNone')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CertificationsSection;